import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, AlertTriangle, XCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  canRetry?: boolean;
  suggestedActions?: string[];
  onRetry?: () => void;
  onDismiss?: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  canRetry = false,
  suggestedActions = [],
  onRetry,
  onDismiss
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [timeLeft, setTimeLeft] = useState(duration);

  useEffect(() => {
    if (duration > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 100) {
            setIsVisible(false);
            setTimeout(() => onDismiss?.(id), 300);
            return 0;
          }
          return prev - 100;
        });
      }, 100);

      return () => clearInterval(timer);
    }
  }, [duration, id, onDismiss]);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => onDismiss?.(id), 300);
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5" />;
      case 'error':
        return <XCircle className="w-5 h-5" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      case 'info':
        return <Info className="w-5 h-5" />;
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const getColors = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-green-500/10',
          border: 'border-green-500/20',
          icon: 'text-green-400',
          progress: 'bg-green-400'
        };
      case 'error':
        return {
          bg: 'bg-red-500/10',
          border: 'border-red-500/20',
          icon: 'text-red-400',
          progress: 'bg-red-400'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-500/10',
          border: 'border-yellow-500/20',
          icon: 'text-yellow-400',
          progress: 'bg-yellow-400'
        };
      case 'info':
        return {
          bg: 'bg-blue-500/10',
          border: 'border-blue-500/20',
          icon: 'text-blue-400',
          progress: 'bg-blue-400'
        };
      default:
        return {
          bg: 'bg-gray-500/10',
          border: 'border-gray-500/20',
          icon: 'text-gray-400',
          progress: 'bg-gray-400'
        };
    }
  };

  const colors = getColors();
  const progressPercentage = duration > 0 ? (timeLeft / duration) * 100 : 0;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, x: 300, scale: 0.3 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 300, scale: 0.5, transition: { duration: 0.2 } }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className={`relative glass-card p-4 border ${colors.bg} ${colors.border} max-w-sm w-full backdrop-blur-lg`}
        >
          {/* Progress bar */}
          {duration > 0 && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10 rounded-b-lg overflow-hidden">
              <motion.div
                className={`h-full ${colors.progress}`}
                initial={{ width: '100%' }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 0.1, ease: 'linear' }}
              />
            </div>
          )}

          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className={`${colors.icon} flex-shrink-0 mt-0.5`}>
              {getIcon()}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h4 className="text-white font-medium text-sm leading-5">
                {title}
              </h4>
              
              {message && (
                <p className="text-gray-300 text-xs mt-1 leading-4">
                  {message}
                </p>
              )}

              {/* Suggested Actions */}
              {suggestedActions.length > 0 && (
                <div className="mt-2">
                  <p className="text-gray-400 text-xs mb-1">Try:</p>
                  <ul className="text-gray-300 text-xs space-y-0.5">
                    {suggestedActions.slice(0, 2).map((action, index) => (
                      <li key={index} className="flex items-center gap-1">
                        <span className="w-1 h-1 bg-gray-400 rounded-full flex-shrink-0" />
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Actions */}
              {canRetry && onRetry && (
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={onRetry}
                    className="text-xs bg-accent hover:bg-accent/80 text-white px-3 py-1.5 rounded-md transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>

            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-white transition-colors flex-shrink-0 p-1 -m-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Toast;
